/*
  LIOR cart drawer + on-screen keyboard (v3, 2026-10-01 evening)

  v1 (2026-10-01, owner bug, iPhone Safari): tapping "Enter discount code" opened the keyboard on top of
  the drawer footer (discount box, subtotal, checkout). The drawer is position:fixed at the full layout
  viewport height; iOS Safari and Android Chrome 108+ shrink only the *visual* viewport for the keyboard.
  Fix: while a text field in the drawer is focused and the keyboard is open, fit the panel
  (.drawer__inner: header / scrolling body / footer) to the visible area, restore exactly afterwards.

  v3 (owner, Chrome for iPhone: "doesn't zoom in but it JUMPS when the discount code section is clicked
  and only comes back after typing"):
  1. Keyboard detection no longer depends on innerHeight. Chrome's new iOS UI shrinks the layout viewport
     itself above the keyboard (innerHeight, 100svh and the fixed drawer all shrink). v1 then decided "no
     keyboard", dropped the fit, and the theme's (max-height:650px) rule turned the whole panel into the
     scroller, pushing the footer with the discount field and CHECK OUT out of view until typing scrolled
     the caret back. Now the visible area is compared with what it was when the field got focus (and with
     the fixed drawer's own box), so a shrinking page, a shrinking visual viewport and a panned visual
     viewport are all handled the same way.
  2. The panel is placed with height = min(visible height, drawer height) and
     margin-top = clamp(offsetTop, 0, drawer height - height): never "lifted twice", never pushed out of the
     drawer box (a stale offsetTop could shift the footer under the keyboard).
  3. Re-measures every animation frame for 1.5s after focus and after every change or viewport event, plus a
     check every 250ms while a field is focused. Chrome on iPhone can change the geometry without firing any
     event (WebKit bug 267545); v1 only re-checked on events and at 120/300/600/1000ms, so a late silent
     change stayed wrong until a keystroke fired an event.
  4. iPhone/iPad only: a tap on a drawer text field is turned into focus({preventScroll:true}) with the
     field moved off-screen for that synchronous call (Base UI / vaul technique), then a click is re-sent so
     theme handlers still run. That stops WebKit's own "scroll the focused field into view" pan, which is
     the jump itself. Not used when the field is already focused (caret placement), on long-press, drag,
     multi-touch, pinch-zoom, or where preventScroll is not supported.
  5. iPhone/iPad only: while a drawer field is focused, a page (window) scroll made by the browser is put
     back to where it was at focus time.
  Everything is restored exactly on blur / keyboard close, survives drawer re-renders (event delegation),
  and does nothing on desktop (no touch) or where window.visualViewport is missing.

  Review fixes (2026-10-02): the frame-loop handle remembers whether it came from requestAnimationFrame or
  setTimeout (v3 passed rAF ids to clearTimeout on release, which can cancel an unrelated page timer with the
  same number); the off-screen focus trick undoes only its own three properties when a focus handler changed
  the field's inline style during the focus call.
*/
(function () {
  'use strict';
  try {
    if (window.__liorDrawerKeyboard) return;
    window.__liorDrawerKeyboard = 3;
  } catch (e) {
    return;
  }

  var ROOT = 'cart-drawer';
  var PANEL = '.drawer__inner';
  var NON_TEXT = /^(hidden|checkbox|radio|button|submit|reset|image|file|range|color)$/i;
  var TAP_TYPES = /^(text|search|email|tel|url|number|password)$/i;
  var MIN_KEYBOARD = 90; // v1 absolute test: px the visual viewport must be shorter than the page
  var EPS = 2; // px: smallest change of the visible area (vs. focus time) that counts as "keyboard / pan"
  var MIN_BODY = 48; // if header + footer leave less than this for the body, scroll the whole panel instead
  var RELEASE_MS = 600; // after blur, keep the layout still while a tap (e.g. "Apply") completes
  var BURST_MS = 1500; // measure every frame this long after focus / any change / any viewport event
  var WATCH_MS = 250; // then keep checking at this interval while a drawer field is focused
  var TAP_SLOP = 10; // px a finger may move and still count as a tap
  var TAP_MAX_MS = 500; // longer presses are left to the browser (magnifier, paste menu)
  var MAX_SCROLL_FIXES = 20; // per focus session; never fight the browser forever
  var state = null;
  var tap = null;

  function now() {
    return Date.now ? Date.now() : new Date().getTime();
  }

  function getVV() {
    try {
      return window.visualViewport || null;
    } catch (e) {
      return null;
    }
  }

  function isTouch() {
    try {
      return (navigator.maxTouchPoints || 0) > 0 || 'ontouchstart' in window;
    } catch (e) {
      return false;
    }
  }

  var IOS = (function () {
    try {
      var n = navigator, ua = n.userAgent || '';
      return /iPad|iPhone|iPod/.test(ua) || (n.platform === 'MacIntel' && (n.maxTouchPoints || 0) > 1);
    } catch (e) {
      return false;
    }
  })();

  var PREVENT_SCROLL = (function () {
    // engines that know FocusOptions read the dictionary member; older ones ignore the argument
    var ok = false;
    try {
      document.createElement('div').focus({
        get preventScroll() {
          ok = true;
          return true;
        }
      });
    } catch (e) {}
    return ok;
  })();

  function raf(fn) {
    // returns a handle that remembers which API made it: rAF ids and timer ids are separate counters, so
    // clearTimeout(rafId) would cancel an unrelated page timer that happens to have the same number
    try {
      if (window.requestAnimationFrame) return { r: window.requestAnimationFrame(fn) };
    } catch (e) {}
    return { t: setTimeout(fn, 16) };
  }

  function caf(h) {
    if (!h) return;
    try {
      if (h.r && window.cancelAnimationFrame) window.cancelAnimationFrame(h.r);
    } catch (e) {}
    try {
      if (h.t) clearTimeout(h.t);
    } catch (e) {}
  }

  function isDrawerField(el) {
    try {
      if (!el || el.nodeType !== 1 || !el.closest) return false;
      var tag = el.tagName;
      if (tag === 'INPUT') {
        if (NON_TEXT.test(el.type || '')) return false;
      } else if (tag !== 'TEXTAREA' && tag !== 'SELECT' && !el.isContentEditable) {
        return false;
      }
      return !!(el.closest(ROOT) && el.closest(PANEL));
    } catch (e) {
      return false;
    }
  }

  function part(panel, selector) {
    try {
      var kids = panel.children;
      for (var i = 0; i < kids.length; i++) {
        if (kids[i].matches && kids[i].matches(selector)) return kids[i];
      }
      return panel.querySelector(selector);
    } catch (e) {
      return null;
    }
  }

  function setAttr(el, attr) {
    if (attr === null) el.removeAttribute('style');
    else el.setAttribute('style', attr);
  }

  /* ---------- inline-style bookkeeping (exact restore) ---------- */

  function record(el) {
    for (var i = 0; i < state.saved.length; i++) {
      if (state.saved[i].el === el) return state.saved[i];
    }
    var rec = { el: el, attr: el.getAttribute('style'), props: {}, last: null };
    state.saved.push(rec);
    return rec;
  }

  function setStyle(el, prop, value) {
    if (!el) return;
    var rec = record(el);
    if (!Object.prototype.hasOwnProperty.call(rec.props, prop)) {
      rec.props[prop] = [el.style.getPropertyValue(prop), el.style.getPropertyPriority(prop)];
    }
    if (el.style.getPropertyValue(prop) !== value || el.style.getPropertyPriority(prop) !== 'important') {
      el.style.setProperty(prop, value, 'important');
    }
    rec.last = el.getAttribute('style');
  }

  function restoreStyles() {
    if (!state) return;
    var saved = state.saved;
    state.saved = [];
    for (var i = 0; i < saved.length; i++) {
      var r = saved[i];
      try {
        if (r.el.getAttribute('style') === r.last) {
          // nobody else touched the attribute while we were active: put it back verbatim
          setAttr(r.el, r.attr);
        } else {
          // the theme changed other inline styles meanwhile: undo only our properties
          for (var p in r.props) {
            if (!Object.prototype.hasOwnProperty.call(r.props, p)) continue;
            if (r.props[p][0]) r.el.style.setProperty(p, r.props[p][0], r.props[p][1]);
            else r.el.style.removeProperty(p);
          }
          if (r.attr === null && r.el.getAttribute('style') === '') r.el.removeAttribute('style');
        }
      } catch (e) {}
    }
  }

  /* ---------- geometry ---------- */

  function sample() {
    // everything in CSS px, client (layout viewport) coordinates
    var vv = getVV();
    if (!vv || !state) return null;
    var de = document.documentElement;
    var s = {
      vh: +vv.height || 0,
      vt: +vv.offsetTop || 0,
      sc: +vv.scale || 1,
      ih: window.innerHeight || 0,
      ch: (de && de.clientHeight) || 0,
      sx: window.pageXOffset || 0,
      sy: window.pageYOffset || 0,
      ct: 0,
      cb: 0
    };
    var root = null;
    try {
      root = (state.panel && state.panel.closest(ROOT)) || document.querySelector(ROOT);
    } catch (e) {}
    var r = null;
    try {
      r = root && root.getBoundingClientRect();
    } catch (e) {}
    if (r && r.height > 0) {
      // the fixed drawer box the panel lives in (height:100% of the layout viewport)
      s.ct = r.top;
      s.cb = r.bottom;
    } else {
      s.cb = Math.max(s.ih, s.ch);
    }
    return s;
  }

  function legacyOpen(s) {
    // v1 test: the visual viewport is clearly shorter than the page (Safari / Android with the keyboard up)
    var ref = Math.max(s.ih, s.ch);
    var visible = s.vh * s.sc;
    if (!ref || !visible) return false;
    return ref - visible > Math.max(MIN_KEYBOARD, ref * 0.15);
  }

  function baseline(s) {
    return s
      ? { gapTop: s.vt - s.ct, gapBottom: s.cb - (s.vt + s.vh), c: s.cb - s.ct, sc: s.sc }
      : { gapTop: 0, gapBottom: 0, c: 0, sc: 1 };
  }

  function plan(s) {
    // where the panel has to be so it covers exactly the visible part of the drawer box, or null
    var b = state.base;
    var c = s.cb - s.ct;
    if (!(s.vh > 0) || !(c > 0)) return null;
    var gapTop = s.vt - s.ct;
    var gapBottom = s.cb - (s.vt + s.vh);
    var zoomed = Math.abs(s.sc - 1) > 0.01 || Math.abs(s.sc - b.sc) > 0.01;
    var moved =
      !zoomed &&
      (gapTop > b.gapTop + EPS || gapBottom > b.gapBottom + EPS || (b.c && c < b.c - EPS));
    if (!moved && !legacyOpen(s)) return null;
    var h = Math.min(s.vh, c);
    var top = Math.min(Math.max(gapTop, 0), c - h);
    return { h: Math.max(0, Math.round(h * 100) / 100), top: Math.max(0, Math.round(top * 100) / 100) };
  }

  function reveal(field, panel) {
    // keep the focused field visible inside the drawer (like scrollIntoView({block:'nearest'}),
    // but only scrolls containers inside the drawer, never the page or the visual viewport)
    try {
      var pad = 12;
      var fr = field.getBoundingClientRect();
      for (var c = field.parentElement; c; c = c.parentElement) {
        var oy = window.getComputedStyle(c).overflowY;
        if ((oy === 'auto' || oy === 'scroll') && c.scrollHeight > c.clientHeight + 1) {
          var cr = c.getBoundingClientRect();
          var d = 0;
          if (fr.top - pad < cr.top) d = fr.top - pad - cr.top;
          else if (fr.bottom + pad > cr.bottom) d = Math.min(fr.bottom + pad - cr.bottom, fr.top - pad - cr.top);
          if (d) {
            c.scrollTop += d;
            fr = field.getBoundingClientRect();
          }
        }
        if (c === panel) break;
      }
    } catch (e) {}
  }

  function apply(p, panel) {
    var h = p.h;
    var top = p.top;
    var header = part(panel, '.drawer__header');
    var body = part(panel, '.cart-drawer__body');
    var footer = part(panel, '.drawer__footer');
    var fixed = (header ? header.getBoundingClientRect().height : 0) + (footer ? footer.getBoundingClientRect().height : 0);
    var mode = body && fixed + MIN_BODY <= h ? 'pin' : 'scroll';

    if (state.mode && state.mode !== mode) restoreStyles();
    state.mode = mode;

    setStyle(panel, 'height', h + 'px');
    setStyle(panel, 'margin-top', top + 'px');
    if (mode === 'pin') {
      // header + footer stay put, the body (items / upsells) scrolls between them
      setStyle(panel, 'overflow', 'hidden');
      setStyle(body, 'overflow-y', 'auto');
      setStyle(body, 'min-height', '0px');
      if (header) setStyle(header, 'flex-shrink', '0');
      if (footer) setStyle(footer, 'flex-shrink', '0');
      if (panel.scrollTop) panel.scrollTop = 0;
    } else {
      // very short visible area: let the whole panel scroll (the theme's own small-screen behaviour)
      setStyle(panel, 'overflow-y', 'auto');
      if (body) {
        setStyle(body, 'overflow', 'visible');
        setStyle(body, 'flex', '0 0 auto');
      }
    }
    state.applied = true;
    watch(header, footer);

    if (state.needReveal || state.lastH !== h || state.lastTop !== top) {
      state.needReveal = false;
      state.lastH = h;
      state.lastTop = top;
      if (state.field) reveal(state.field, panel);
    }
  }

  function watch(header, footer) {
    // the footer can grow while typing (e.g. "This code isn't valid" message): re-check the fit
    try {
      if (!footer || state.watched === footer || !window.ResizeObserver) return;
      if (!state.ro) state.ro = new window.ResizeObserver(kick);
      state.ro.disconnect();
      state.ro.observe(footer);
      if (header) state.ro.observe(header);
      state.watched = footer;
    } catch (e) {}
  }

  function unwatch() {
    try {
      if (state && state.ro) state.ro.disconnect();
      if (state) state.watched = null;
    } catch (e) {}
  }

  function unapply() {
    if (!state || !state.applied) return;
    unwatch();
    var panel = state.panel;
    var scroll = state.panelScroll || 0;
    restoreStyles();
    state.applied = false;
    state.mode = null;
    state.lastH = state.lastTop = null;
    state.needReveal = true;
    try {
      // also put back 0: on short screens the panel itself scrolls, and reveal() or the browser moved it
      if (panel && panel.isConnected && panel.scrollTop !== scroll) panel.scrollTop = scroll;
    } catch (e) {}
  }

  /* ---------- page scroll pin (iOS) ---------- */

  function scrollToInstant(x, y) {
    try {
      window.scrollTo({ left: x, top: y, behavior: 'instant' });
      return;
    } catch (e) {}
    // engines without behavior:'instant': switch off the theme's smooth scrolling for this one call
    var de = document.documentElement, attr = null;
    try {
      attr = de.getAttribute('style');
      de.style.setProperty('scroll-behavior', 'auto', 'important');
    } catch (e) {}
    try {
      window.scrollTo(x, y);
    } catch (e) {}
    try {
      setAttr(de, attr);
      if (attr === null && de.getAttribute('style') === '') de.removeAttribute('style');
    } catch (e) {}
  }

  function pinScroll(s) {
    // WebKit's "reveal the focused field" can scroll the (locked) page; the drawer is modal, so any page
    // scroll while its field is focused is spurious: put it back where it was at focus time
    if (!IOS || !state.field || !state.baseScroll || state.scrollFixes >= MAX_SCROLL_FIXES) return false;
    var b = state.baseScroll;
    if (Math.abs(s.sx - b.x) < 1 && Math.abs(s.sy - b.y) < 1) return false;
    state.scrollFixes++;
    scrollToInstant(b.x, b.y);
    state.needReveal = true;
    return true;
  }

  /* ---------- lifecycle ---------- */

  function kick() {
    // something may have changed: measure every frame for the next BURST_MS
    if (!state) return;
    state.burstUntil = now() + BURST_MS;
    if (!state.poll) state.poll = raf(step);
  }

  function step() {
    if (!state) return;
    state.poll = null;
    update();
    if (state && !state.poll && now() < state.burstUntil) state.poll = raf(step);
  }

  function later(ms) {
    if (!state) return;
    state.timers.push(setTimeout(kick, ms));
  }

  function setPanel(panel) {
    unapply();
    state.panel = panel;
    // how the drawer was scrolled before the keyboard (or the browser's focus reveal) moved anything;
    // put back when the fit is released
    try {
      state.panelScroll = panel ? panel.scrollTop : 0;
    } catch (e) {
      state.panelScroll = 0;
    }
  }

  function retarget(field) {
    var panel = field.closest(PANEL);
    if (panel !== state.panel) setPanel(panel);
    state.field = field;
    state.releaseAt = 0;
    state.needReveal = true;
  }

  function listen(on) {
    var s = state;
    var m = on ? 'addEventListener' : 'removeEventListener';
    try {
      if (s.vv) {
        s.vv[m]('resize', kick);
        s.vv[m]('scroll', kick);
      }
      window[m]('resize', kick);
      window[m]('scroll', kick);
      window[m]('orientationchange', kick);
      document[m]('input', kick, true);
    } catch (e) {}
  }

  function release() {
    if (!state) return;
    var s = state;
    try {
      unapply();
      unwatch();
    } catch (e) {}
    try {
      listen(false);
      caf(s.poll);
      s.poll = null;
      if (s.iv) clearInterval(s.iv);
      for (var i = 0; i < s.timers.length; i++) clearTimeout(s.timers[i]);
    } catch (e) {}
    state = null;
  }

  function update() {
    if (!state) return;
    try {
      var field = state.field;
      if (field && (!field.isConnected || document.activeElement !== field)) {
        var active = document.activeElement;
        if (isDrawerField(active)) {
          retarget(active);
          field = active;
        } else {
          // focus left the drawer, or the theme re-rendered it and removed the focused field
          state.field = field = null;
          if (!state.releaseAt) {
            state.releaseAt = now() + RELEASE_MS;
            later(RELEASE_MS + 20);
          }
        }
      }
      if (state.panel && !state.panel.isConnected) {
        // the theme re-rendered the drawer: drop the old (detached) panel
        setPanel(field ? field.closest(PANEL) : null);
      }
      var s = sample();
      if (s && pinScroll(s)) s = sample();
      var sig = s ? [s.vh, s.vt, s.sc, s.ih, s.ch, s.sy, s.ct, s.cb].join('|') : '';
      if (sig !== state.sig) {
        // any change (with or without an event): follow it frame by frame for a while
        state.sig = sig;
        kick();
      }
      var p = s && state.panel ? plan(s) : null;
      if (!field && (!p || now() >= state.releaseAt)) {
        release();
        return;
      }
      if (p) apply(p, state.panel);
      else unapply();
    } catch (e) {
      try {
        release();
      } catch (e2) {}
    }
  }

  function onFocusIn(event) {
    try {
      var field = event.target;
      if (!isDrawerField(field)) return;
      var vv = getVV();
      if (!vv || !isTouch()) return;
      if (state) {
        retarget(field);
      } else {
        state = {
          field: null,
          panel: null,
          vv: vv,
          saved: [],
          timers: [],
          poll: null,
          iv: 0,
          burstUntil: 0,
          sig: null,
          base: null,
          baseScroll: null,
          scrollFixes: 0,
          applied: false,
          mode: null,
          panelScroll: 0,
          releaseAt: 0,
          needReveal: true,
          lastH: null,
          lastTop: null,
          ro: null,
          watched: null
        };
        retarget(field);
        // the visible area right now (before the keyboard) is the reference for "moved / shrank"
        state.base = baseline(sample());
        state.baseScroll = { x: window.pageXOffset || 0, y: window.pageYOffset || 0 };
        listen(true);
        try {
          state.iv = setInterval(function () {
            if (state && state.field) update();
          }, WATCH_MS);
        } catch (e) {}
      }
      kick();
    } catch (e) {}
  }

  function onFocusOut(event) {
    try {
      if (!state || !isDrawerField(event.target)) return;
      setTimeout(function () {
        try {
          if (!state) return;
          var active = document.activeElement;
          if (isDrawerField(active)) {
            retarget(active);
          } else {
            state.field = null;
            state.releaseAt = now() + RELEASE_MS;
            later(RELEASE_MS + 20);
          }
          kick();
        } catch (e) {}
      }, 0);
    } catch (e) {}
  }

  /* ---------- iOS: focus a tapped drawer field without WebKit's reveal scroll ---------- */

  function isTapInput(el) {
    if (!el || el.nodeType !== 1 || el.disabled || el.readOnly) return false;
    if (el.tagName === 'TEXTAREA') return true;
    return el.tagName === 'INPUT' && TAP_TYPES.test(el.type || 'text');
  }

  function tapField(el) {
    try {
      if (el && el.nodeType === 3) el = el.parentNode;
      if (!el || el.nodeType !== 1 || !el.closest) return null;
      var f = el;
      if (!isTapInput(f)) {
        var label = el.closest('label');
        f = label && label.control;
        if (!isTapInput(f)) return null;
      }
      return isDrawerField(f) ? f : null;
    } catch (e) {
      return null;
    }
  }

  function focusNoScroll(f) {
    // WebKit skips its reveal scroll for focus({preventScroll:true}); it can still scroll for transformed
    // sheets (the drawer panel is translated), so the field is also moved off-screen for this one
    // synchronous call and restored before anything is painted
    var attr = null, mine = null, prev = {}, moved = false;
    var props = ['transition', 'opacity', 'transform'];
    try {
      attr = f.getAttribute('style');
      for (var i = 0; i < props.length; i++) {
        prev[props[i]] = [f.style.getPropertyValue(props[i]), f.style.getPropertyPriority(props[i])];
      }
      f.style.setProperty('transition', 'none', 'important');
      f.style.setProperty('opacity', '0', 'important');
      f.style.setProperty('transform', 'translateY(-2000px)', 'important');
      mine = f.getAttribute('style');
      moved = true;
    } catch (e) {}
    try {
      f.focus({ preventScroll: true });
    } catch (e) {}
    if (moved) {
      try {
        var untouched = f.getAttribute('style') === mine;
        var put = function (p) {
          if (prev[p][0]) f.style.setProperty(p, prev[p][0], prev[p][1]);
          else f.style.removeProperty(p);
        };
        // opacity/transform back first while transition is still 'none', settle, then the transition itself,
        // so nothing animates back from off-screen. A focus handler may have changed other inline styles of
        // the field meanwhile: then only our three properties are undone.
        if (untouched) {
          setAttr(f, attr);
          f.style.setProperty('transition', 'none', 'important');
        } else {
          put('opacity');
          put('transform');
        }
        void window.getComputedStyle(f).opacity;
        if (untouched) setAttr(f, attr);
        else put('transition');
        // read back (this also flushes the engine's lazy style-attribute sync, which can otherwise re-create an
        // empty style="" later) and drop an empty attribute the field did not have before
        if (attr === null && f.getAttribute('style') === '') f.removeAttribute('style');
      } catch (e) {}
    }
    if (document.activeElement !== f) {
      try {
        f.focus();
      } catch (e) {}
    }
  }

  function onTouchStart(event) {
    tap = null;
    try {
      if (!event.touches || event.touches.length !== 1) return;
      var f = tapField(event.target);
      if (!f) return;
      var t = event.touches[0];
      tap = { field: f, x: t.clientX, y: t.clientY, at: now(), moved: false };
    } catch (e) {
      tap = null;
    }
  }

  function onTouchMove(event) {
    try {
      if (!tap || tap.moved) return;
      var t = event.touches && event.touches[0];
      if (!t || event.touches.length !== 1 || Math.abs(t.clientX - tap.x) > TAP_SLOP || Math.abs(t.clientY - tap.y) > TAP_SLOP) {
        tap.moved = true;
      }
    } catch (e) {
      tap = null;
    }
  }

  function onTouchEnd(event) {
    var tp = tap;
    tap = null;
    try {
      if (!tp || tp.moved || !event.cancelable || event.defaultPrevented) return;
      if ((event.touches && event.touches.length) || now() - tp.at > TAP_MAX_MS) return;
      var f = tp.field;
      // already focused: let the native tap move the caret
      if (!f.isConnected || document.activeElement === f || !isTapInput(f)) return;
      var vv = getVV();
      if (!vv || Math.abs((+vv.scale || 1) - 1) > 0.01) return; // pinch-zoomed: native behaviour
      var ct = event.changedTouches && event.changedTouches[0];
      if (!ct) return;
      var hit = document.elementFromPoint(ct.clientX, ct.clientY);
      if (tapField(hit) !== f) return; // finger lifted somewhere else: leave it to the browser
      event.preventDefault(); // no native focus (and its reveal scroll), no compatibility mouse events
      focusNoScroll(f);
      try {
        // the prevented tap has no click any more: re-send one so theme click handlers still run
        hit.dispatchEvent(
          new window.MouseEvent('click', {
            bubbles: true,
            cancelable: true,
            view: window,
            detail: 1,
            screenX: ct.screenX,
            screenY: ct.screenY,
            clientX: ct.clientX,
            clientY: ct.clientY
          })
        );
      } catch (e) {}
      if (hit !== f && document.activeElement === f) {
        // label activation refocuses its control without preventScroll
        try {
          f.focus({ preventScroll: true });
        } catch (e) {}
      }
    } catch (e) {}
  }

  try {
    document.addEventListener('focusin', onFocusIn, true);
    document.addEventListener('focusout', onFocusOut, true);
    if (IOS && PREVENT_SCROLL) {
      document.addEventListener('touchstart', onTouchStart, { capture: true, passive: true });
      document.addEventListener('touchmove', onTouchMove, { capture: true, passive: true });
      document.addEventListener('touchend', onTouchEnd, { capture: true, passive: false });
      document.addEventListener(
        'touchcancel',
        function () {
          tap = null;
        },
        { capture: true, passive: true }
      );
    }
  } catch (e) {}
})();
